import React from "react";
import { Controller } from "react-hook-form";
import Select, { components as rsComponents } from "react-select";
import { CheckBoxCustomStyle } from "../Checkbox/HorseProfile/checkboxStyle";

const SingleSelectField = ({
  name,
  control,
  label,
  options = [],
  className = "",
  placeholder,
  height,
  disabled = false,
  isClearable = true,
  onChange,
  stylesOverride = {},
  components: componentsProp,
  value,
  defaultValue,
  ...rest
}) => {
  const getAllOptions = React.useMemo(() => {
    if (!Array.isArray(options)) return [];
    if (options.some((o) => Array.isArray(o?.options))) {
      return options.flatMap((g) => g?.options ?? []);
    }
    return options;
  }, [options]);

  const toOptionObject = React.useCallback(
    (val) => {
      if (val == null) return null;

      if (typeof val === "object") {
        const primitive = val.value ?? val.id ?? null;
        if (primitive == null) return null;
        const found = getAllOptions.find(
          (opt) => String(opt?.value) === String(primitive)
        );
        return found ?? val;
      }

      const target = String(val);
      return getAllOptions.find((opt) => String(opt?.value) === target) || null;
    },
    [getAllOptions]
  );

  const RadioOption = ({
    children,
    isSelected,
    innerRef,
    innerProps,
    ...restOpt
  }) => (
    <rsComponents.Option
      {...restOpt}
      innerRef={innerRef}
      innerProps={innerProps}
    >
      <div
        className="select-box"
        style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
      >
        <div style={{ display: "flex", alignItems: "center", marginRight: 8 }}>
          <input
            type="radio"
            checked={isSelected}
            readOnly
            disabled={restOpt?.isDisabled}
            name={`radio-${name}`}
            style={{ cursor: "pointer" }}
          />
        </div>
        <span>{children}</span>
      </div>
    </rsComponents.Option>
  );

  const baseStyles = {
    ...CheckBoxCustomStyle,
    control: (base) => ({
      ...base,
      borderColor: "#999",
      boxShadow: "none",
      borderRadius: "9999px",
      height: height ? height : "51.8px",
      cursor: "pointer",
      "&:hover": { borderColor: "#999" },
      "&:focus-within": { borderColor: "#999" },
    }),
    clearIndicator: (base) => ({
      ...base,
      cursor: "pointer",
      marginBottom: 20,
      padding: 0,
    }),
    dropdownIndicator: (base) => ({
      ...base,
      cursor: "pointer",
      marginBottom: "20px !important",
    }),
    placeholder: (base) => ({
      ...base,
      paddingTop: '10px'
    }),
    menuPortal: (base) => ({ ...base, fontFamily: 'Montserrat', zIndex: 9999 }),
  };

  const styles = { ...baseStyles, ...stylesOverride };

  const mergedComponents = {
    Option: RadioOption,
    IndicatorSeparator: () => null,
    ...(componentsProp || {}),
  };

  // Non-RHF-mode state — declared before the RHF branch so the hook runs
  // unconditionally in every render (rules-of-hooks).
  const [inner, setInner] = React.useState(defaultValue ?? null);

  // ---------- RHF MODE ----------
  if (control && name) {
    return (
      <div className={`relative font-montserrat ${className}`}>
        {label && (
          <div className="absolute -top-2 left-3 z-10 text-sm bg-white px-2">
            {label}
          </div>
        )}

        <Controller
          control={control}
          name={name}
          render={({ field }) => {
            const selectedObj = toOptionObject(field.value);
            return (
              <Select
                inputId={name}
                className="focus:border-none focus:ring-0 focus:outline-none !border-none !ring-0"
                classNamePrefix="react-select"
                isMulti={false}
                isClearable={isClearable}
                isSearchable={false}
                closeMenuOnSelect={true}
                placeholder={placeholder}
                options={options}
                value={selectedObj}
                onChange={(sel, actionMeta) => {
                  const val = sel ? sel.value : null;
                  field.onChange(val);
                  if (typeof onChange === "function")
                    onChange(sel, actionMeta, val);
                }}
                onBlur={field.onBlur}
                name={field.name}
                ref={field.ref}
                components={mergedComponents}
                styles={styles}
                isDisabled={disabled}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPosition="fixed"
                menuShouldBlockScroll={false}
                hideSelectedOptions={false}
                getOptionLabel={(o) => o.label}
                getOptionValue={(o) => o.value}
                {...rest}
              />
            );
          }}
        />
      </div>
    );
  }

  // ---------- NON-RHF MODE ----------
  const isControlled = value !== undefined;
  const currentValue = isControlled ? value : inner;
  const selectedObj = toOptionObject(currentValue);

  const handleChange = (sel, actionMeta) => {
    const val = sel ? sel.value : null;
    if (!isControlled) setInner(val);
    if (typeof onChange === "function") onChange(sel, actionMeta, val);
  };

  return (
    <div className={`relative font-montserrat ${className}`}>
      {label && (
        <div className="absolute -top-2 left-3 z-10 text-sm bg-white px-2">
          {label}
        </div>
      )}

      <Select
        inputId={name}
        className="focus:border-none focus:ring-0 focus:outline-none !border-none !ring-0"
        classNamePrefix="react-select"
        isMulti={false}
        isClearable={isClearable}
        isSearchable={false}
        closeMenuOnSelect={true}
        placeholder={placeholder}
        options={options}
        value={selectedObj}
        onChange={handleChange}
        components={mergedComponents}
        styles={styles}
        isDisabled={disabled}
        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
        menuPosition="fixed"
        menuShouldBlockScroll={false}
        hideSelectedOptions={false}
        getOptionLabel={(o) => o.label}
        getOptionValue={(o) => o.value}
        {...rest}
      />
    </div>
  );
};

export default SingleSelectField;
